import { Injectable } from '@angular/core';

export interface ImageCompressionOptions {
  minBytes: number;
  maxEdgePx: number;
  quality: number;
}

const DEFAULT_OPTIONS: ImageCompressionOptions = {
  minBytes: 2_000_000,
  maxEdgePx: 2400,
  quality: 0.75,
};

@Injectable({ providedIn: 'root' })
export class ImageCompressionService {
  shouldCompress(file: File, minBytes: number = DEFAULT_OPTIONS.minBytes): boolean {
    return (file.type === 'image/jpeg' || file.type === 'image/png') && file.size >= minBytes;
  }

  async compress(file: File, opts?: Partial<ImageCompressionOptions>): Promise<File> {
    const options = { ...DEFAULT_OPTIONS, ...opts };
    if (!this.shouldCompress(file, options.minBytes)) return file;

    let bitmap: ImageBitmap | null = null;
    let objectUrl: string | null = null;
    let canvas: HTMLCanvasElement | null = null;

    try {
      if (typeof globalThis.createImageBitmap === 'function') {
        try {
          bitmap = await globalThis.createImageBitmap(file, { imageOrientation: 'from-image' });
        } catch {
          bitmap = null;
        }
      }

      const source = bitmap ?? (await this.loadImage(objectUrl = URL.createObjectURL(file)));
      const scale = Math.min(1, options.maxEdgePx / Math.max(source.width, source.height));
      const width = Math.max(1, Math.round(source.width * scale));
      const height = Math.max(1, Math.round(source.height * scale));

      canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;

      const context = canvas.getContext('2d');
      if (!context) return file;

      context.fillStyle = 'white';
      context.fillRect(0, 0, width, height);
      context.drawImage(source, 0, 0, width, height);

      const blob = await this.toBlob(canvas, options.quality);
      if (!blob || blob.size >= file.size) return file;

      const name = /\.[^/.]+$/.test(file.name) ? file.name.replace(/\.[^/.]+$/, '.jpg') : `${file.name}.jpg`;
      return new File([blob], name, {
        type: 'image/jpeg',
        lastModified: file.lastModified,
      });
    } catch {
      return file;
    } finally {
      try {
        bitmap?.close();
      } catch {
        // Ignore cleanup errors.
      }
      if (objectUrl) {
        try {
          URL.revokeObjectURL(objectUrl);
        } catch {
          // Ignore cleanup errors.
        }
      }
      canvas = null;
    }
  }

  private loadImage(objectUrl: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error('Image decoding failed'));
      image.src = objectUrl;
    });
  }

  private toBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | null> {
    return new Promise((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', quality);
    });
  }
}
